import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import {
  BadgeCopyComponent,
  IconComponent,
  IndicatorState,
  IndicatorStates,
  truncate,
} from '@ui';

@Component({
  selector: 'ui-alarm-center-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeCopyComponent, IconComponent],
  template: `
    <div class="flex gap-1">
      @if (isDanger()) {
        <div class="flex text-error">
          <ui-icon icon="exclamation-circle" theme="fill" />
        </div>
      }
      <div class="flex flex-col gap-y-1">
        <div class="flex gap-x-2">
          @if (showCenterCode()) {
            <span
              class="flex items-center justify-center text-[10px] rounded uppercase px-1 whitespace-nowrap"
              [class]="stateClassMap[state()].code"
            >
              {{ distributorId() }}
            </span>
          }
          <span
            class="text-sm font-medium uppercase duration-300 transition-colors"
            [class]="stateClassMap[state()].label"
            (click)="emitCenter()"
          >
            {{ truncatedName() }}
          </span>
        </div>
        <ui-badge-copy [id]="copy()!" [disabled]="isDisabled()" />
      </div>
    </div>
  `,
})
export class AlarmCenterIndicatorComponent {
  protected readonly TRUNCATE_MAX_CHARS: number = 72;

  state = input<IndicatorState>(IndicatorStates.Default);
  showCenterCode = input<boolean>(false);
  name = input.required<string>();
  distributorId = input.required<string>();
  centerId = input.required<string>();
  rsabtId = input<string>();
  lineId = input<string>();
  tsabtId = input<string>();
  vtnId = input<string>();
  transformerId = input<string>();

  copy = computed(() => {
    if (this.lineId()) {
      return this.lineId();
    }
    return this.centerId();
  });

  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
    lineId?: string;
  }>();

  isDanger = computed(() => this.state() === IndicatorStates.Danger);
  isDisabled = computed(() => this.state() === IndicatorStates.Disabled);
  truncatedName = computed(() => {
    const maxChars = this.showCenterCode()
      ? this.TRUNCATE_MAX_CHARS
      : this.TRUNCATE_MAX_CHARS + 5;
    return truncate(this.name()!, maxChars);
  });

  emitCenter() {
    this.onCenter.emit({
      distributorId: this.distributorId()!,
      centerId: this.centerId()!,
      rsabtId: this.rsabtId()!,
      tsabtId: this.tsabtId()!,
      vtnId: this.vtnId()!,
      lineId: this.lineId()!,
    });
  }

  stateClassMap: Record<IndicatorState, { code: string; label: string }> = {
    active: {
      code: 'text-black bg-[#D6E4FF]',
      label: 'cursor-pointer text-primary hover:text-tertiary',
    },
    disabled: { code: 'bg-neutral-1 text-neutral-6', label: 'text-neutral-6' },
    danger: {
      code: 'text-black bg-red-2',
      label: 'cursor-pointer text-error hover:text-black',
    },
  };
}
